import Icons from "@/src/components/Icons";
import { digitsEnToFa } from "@persian-tools/persian-tools";
import { useEffect, useState } from "react";
import { Cell, Pie, PieChart } from "recharts";

interface scoreProps {
  data: any;
}

function ScoreItem({ data }: scoreProps) {
  const [chartData, setChartData] = useState<any>([]);

  // Calculate total number of ratings
  const totalRatings =
    (data.fiveStar || 0) +
    (data.fourStar || 0) +
    (data.threeStar || 0) +
    (data.twoStar || 0) +
    (data.oneStar || 0);

  // Calculate average score
  const weightedSum =
    5 * (data.fiveStar || 0) +
    4 * (data.fourStar || 0) +
    3 * (data.threeStar || 0) +
    2 * (data.twoStar || 0) +
    1 * (data.oneStar || 0);

  const averageScore =
    totalRatings > 0 ? (weightedSum / totalRatings).toFixed(2) : "0.00";
  // Calculate percentages for each rating
  const calculatePercentage = (count: any) => {
    return totalRatings > 0 ? Math.round((count / totalRatings) * 100) : 0;
  };

  // Prepare ratings data with calculated percentages
  const ratings = [
    {
      value: 5,
      count: data.fiveStar || 0,
      percentage: calculatePercentage(data.fiveStar || 0),
      color: "#ff617d",
    },
    {
      value: 4,
      count: data.fourStar || 0,
      percentage: calculatePercentage(data.fourStar || 0),
      color: "#D84099",
    },
    {
      value: 3,
      count: data.threeStar || 0,
      percentage: calculatePercentage(data.threeStar || 0),
      color: "#b554c8",
    },
    {
      value: 2,
      count: data.twoStar || 0,
      percentage: calculatePercentage(data.twoStar || 0),
      color: "#6e68d6",
    },
    {
      value: 1,
      count: data.oneStar || 0,
      percentage: calculatePercentage(data.oneStar || 0),
      color: "#236ec9",
    },
  ];
  useEffect(() => {
    if (totalRatings > 0) {
      setChartData([
        {
          name: "۵ ستاره",
          value: calculatePercentage(data.fiveStar || 0),
          color: "#ff617d",
        },
        {
          name: "۴ ستاره",
          value: calculatePercentage(data.fourStar || 0),
          color: "#D84099",
        },
        {
          name: "۳ ستاره",
          value: calculatePercentage(data.threeStar || 0),
          color: "#b554c8",
        },
        {
          name: "۲ ستاره",
          value: calculatePercentage(data.twoStar || 0),
          color: "#6e68d6",
        },
        {
          name: "۱ ستاره",
          value: calculatePercentage(data.oneStar || 0),
          color: "#236ec9",
        },
      ]);
    } else {
      setChartData([]);
    }
  }, [data, totalRatings]);

  return (
    <div className="shadow-md border border-gray-100-mmpz mt-4 rounded-xl min-h-[228px]">
      <div className="h-[66px] bg-gray-100-mmpz rounded-t-xl p-2 flex flex-col gap-2">
        <div className="flex w-full justify-between">
          <p className="text-sm font-medium">{data.foodName}</p>
          <div className="flex items-center">
            <p className="text-sm font-medium ml-1">
              {digitsEnToFa(averageScore)}
            </p>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
                fill="#FFD700"
              />
            </svg>
          </div>
        </div>
        <div className="flex w-full justify-between">
          <p className="text-sm">تعداد نظر های ثبت شده:</p>
          <p className="text-sm">
            {digitsEnToFa(totalRatings)} <small>نفر</small>
          </p>
        </div>
      </div>
      <div className="p-4 flex flex-row">
        {/* Rating breakdown */}
        <div className="w-2/3 pl-4">
          {ratings.map((rating) => (
            <div
              key={rating.value}
              className="flex items-center mb-2 justify-end"
            >
              <div className="flex items-center ml-2">
                <span className="text-sm font-semibold ml-2">
                  {digitsEnToFa(rating.value)}
                </span>
                <Icons name="star" size={16} />
              </div>
              <div className="flex-grow mx-2 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${rating.percentage}%`,
                    backgroundColor: rating.color,
                  }}
                ></div>
              </div>
              <div className="text-right ml-2 w-16 text-xs">
                {digitsEnToFa(rating.count)}
              </div>
            </div>
          ))}
        </div>
        {/* Donut chart */}
        <div className="w-1/3 relative flex items-center justify-center">
          <PieChart width={120} height={120}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={30}
              outerRadius={50}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
            >
              {chartData.map((entry : any, index : any) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </div>
      </div>
    </div>
  );
}

export default ScoreItem;
